import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPIHousien } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export interface PaymentMethod {
  brand: string;
  last_four: string;
  holder: string;
  type: string;
}

/**
 * The subscribed plan, from the catalogue.
 *
 * `display_name` is a locale map on v3 plans and a plain string on legacy ones,
 * so never read `.pt_br` without checking. `limits` carries `interactions` and
 * `partnership_searches` only — the seat limit lives in `max_seats`.
 */
export interface SubscriptionPlanBlock {
  id: string | null;
  slug: string | null;
  segment: string | null;
  display_name: Record<string, string> | string | null;
  price_cents: number | null;
  currency: string | null;
  max_seats: number | null;
  limits: Record<string, number> | null;
  features: Record<string, boolean> | null;
  /** What one seat of the plan gets each month (interactions, partnership searches). */
  seat_limits?: Record<string, number> | null;
}

/**
 * What Payien reports being charged.
 *
 * `null` on either field means Payien did not report it — check
 * `data_freshness`. That is different from `0`, which means genuinely free.
 */
export interface SubscriptionBillingBlock {
  price_cents: number | null;
  billing_frequency: string | null;
  /**
   * The plan name Payien reports charging for — not the catalogue's, and the
   * only one available when `plan` is null.
   */
  plan_name: string | null;
  /** The pro-rata on the seat controls divides by this. */
  billing_cycle_days?: number | null;
}

/**
 * The coupon Payien is still honouring on this subscription.
 *
 * Frozen at checkout and spent one renewal at a time. `remaining_cycles` is a
 * sentinel, not a plain count: `0` is a coupon that has run out, `-1` is one
 * that never does, and anything above zero is a renewal it still covers.
 */
export interface ActiveDiscount {
  coupon_code: string | null;
  /** `percentage`, `fixed` or `free`, as Payien spells it. */
  discount_type: string | null;
  /** Percent for `percentage`, cents for `fixed`. */
  discount_value: number | null;
  duration: string | null;
  remaining_cycles: number | null;
}

export interface SubscriptionDetail {
  subscription_id: string;
  /**
   * What the subscription is made of, one entry per plan. `unit_price_cents`
   * is what Payien charges per unit of that item, which is per seat on an
   * agency plan and the whole price on a solo one.
   */
  items?: Array<{ plan_id: string; quantity: number; unit_price_cents?: number | null }>;
  /** The smaller list that replaces `items` at the renewal. */
  scheduled_items_change?: {
    items: Array<{ plan_id: string; quantity: number }>;
    effective_at: string;
  } | null;
  /** The subscribed plan; null when its plan_id is not in the catalogue. */
  plan: SubscriptionPlanBlock | null;
  /** What Payien reports charging. */
  billing: SubscriptionBillingBlock;
  subscription_status:
    | 'active'
    | 'trialing'
    | 'pending'
    | 'suspended'
    | 'cancelled'
    | 'expired'
    | 'pending_upgrade';
  payment_status:
    | 'pending'
    | 'approved'
    | 'failed'
    | 'refunded'
    | 'cancelled'
    | 'chargedback';
  start_date?: string;
  end_date?: string;
  next_renewal_date?: string;
  consecutive_failures: number;
  scheduled_plan_change?: {
    plan_id: string;
    name: string;
    price_cents: number;
    effective_at: string;
  } | null;
  cancelled_at?: string | null;
  /** The owner asked to cancel; access runs to `next_renewal_date` (BUG-048). */
  cancel_at_period_end?: boolean;
  cancellation_requested_at?: string | null;
  payment_method?: PaymentMethod | null;
  /**
   * The coupon still running, when there is one.
   *
   * Null also when Payien is unavailable — check `data_freshness` before
   * reading an absent coupon as the agency having none.
   */
  active_discount?: ActiveDiscount | null;
  data_freshness?: 'fresh' | 'stale';
}

export interface InvoiceItem {
  invoice_id: string;
  date: string;
  period_start?: string;
  period_end?: string;
  amount_cents: number;
  amount_paid_cents: number;
  status: 'paid' | 'pending' | 'overdue' | 'cancelled' | 'refunded';
  nfse_url?: string | null;
  nfse_number?: string | null;
  card_last_four?: string | null;
  invoice_url?: string | null;
}

export interface InvoicesResponse {
  invoices: InvoiceItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface ChangePlanPreviewRequest {
  plan_id: string;
  price_cents: number;
  billing_frequency: string;
  billing_cycle_days: number;
}

export interface ChangePlanPreviewResponse {
  pro_rata_amount_cents: number;
  days_remaining: number;
  billing_cycle_days: number;
  current_plan_id: string;
  target_plan_id: string;
  next_renewal_date: string;
  /**
   * Only in the free period (`pro_rata_amount_cents` is then 0): the price of
   * the new plan, charged on `next_renewal_date`. Absent once active.
   */
  next_charge_amount_cents?: number;
  /**
   * True when this plan change cancels a user reduction that was scheduled
   * for the next renewal. Absent or false otherwise.
   */
  scheduled_seat_change_cleared?: boolean;
}

export interface ChangePlanRequest {
  plan_id: string;
  price_cents: number;
  billing_frequency: string;
  billing_cycle_days: number;
  name: string;
  charge_upgrade_difference: boolean;
}

export interface ChangePlanResponse {
  subscription_id: string;
  plan_id: string;
  subscription_status: string;
  charge_id?: string | null;
  effective_at?: string | null;
  next_renewal_date: string;
  billing_snapshot?: { name: string; price_cents: number };
}

export interface CancelSubscriptionResponse {
  subscription_id: string;
  /** Unchanged (trialing or active) when the access runs to the renewal date. */
  subscription_status: string;
  cancel_at_period_end?: boolean;
  /** When the scheduled cancellation takes effect. */
  cancels_at?: string | null;
  cancelled_at: string | null;
}

export interface RevertCancellationResponse {
  subscription_id: string;
  subscription_status: string;
  cancel_at_period_end: boolean;
}

export interface PaymentMethodSessionResponse {
  session_id: string;
  form_url: string;
  expires_at: string;
}

/**
 * Retrieves the agency's active subscription detail via
 * `GET /agencies/{agencyId}/subscription`.
 *
 * @param agencyId - The agency identifier
 * @returns A cancelable promise resolving to the subscription detail, including
 *   plan, status, payment method, and any scheduled plan change
 */
/**
 * Fetches an agency's current subscription detail via
 * `GET /agencies/{agencyId}/subscription`.
 *
 * @param agencyId - The agency identifier
 * @returns A cancelable promise resolving to the subscription detail
 */
export function getSubscriptionDetail(
  agencyId: string,
): CancelablePromise<SubscriptionDetail> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agencies/{agencyId}/subscription',
    path: { agencyId },
  });
}

/**
 * Cancels the agency's subscription via `DELETE /agencies/{agencyId}/subscription`.
 *
 * @param agencyId - The agency identifier
 * @returns A cancelable promise resolving to the cancellation result and timestamp
 */
/**
 * Cancels an agency's active subscription via
 * `DELETE /agencies/{agencyId}/subscription`.
 *
 * @param agencyId - The agency identifier
 * @returns A cancelable promise resolving to the cancellation outcome
 */
export function cancelSubscription(
  agencyId: string,
): CancelablePromise<CancelSubscriptionResponse> {
  return __request(OpenAPIHousien, {
    method: 'DELETE',
    url: '/agencies/{agencyId}/subscription',
    path: { agencyId },
  });
}

/**
 * Keeps a subscription whose cancellation was scheduled, via
 * `POST /agencies/{agencyId}/subscription/revert-cancellation`.
 *
 * @param agencyId - The agency identifier
 * @returns A cancelable promise resolving to the subscription, no longer ending
 */
export function revertCancellation(
  agencyId: string,
): CancelablePromise<RevertCancellationResponse> {
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agencies/{agencyId}/subscription/revert-cancellation',
    path: { agencyId },
  });
}

/**
 * Changes the agency's subscription plan via
 * `POST /agencies/{agencyId}/subscription/change-plan`.
 *
 * @param agencyId - The agency identifier
 * @param requestBody - The target plan, pricing, billing frequency, and whether
 *   to charge the upgrade difference immediately
 * @returns A cancelable promise resolving to the change-plan result, including
 *   the (possibly scheduled) effective date and next renewal date
 */
/**
 * Changes an agency's subscription plan via
 * `POST /agencies/{agencyId}/subscription/change-plan`.
 *
 * @param agencyId - The agency identifier
 * @param requestBody - The target plan and change parameters
 * @returns A cancelable promise resolving to the plan-change result
 */
export function changePlan(
  agencyId: string,
  requestBody: ChangePlanRequest,
): CancelablePromise<ChangePlanResponse> {
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agencies/{agencyId}/subscription/change-plan',
    path: { agencyId },
    body: requestBody,
    mediaType: 'application/json',
  });
}

/**
 * Creates a payment-method-update session via
 * `POST /agencies/{agencyId}/subscription/payment-method-session`.
 *
 * The response's `form_url` is a Payien-hosted, PCI-compliant card form meant
 * to be embedded in an iframe — no card data is sent through this call.
 *
 * @param agencyId - The agency identifier
 * @param idempotencyKey - Optional key identifying one user attempt. Reusing it
 *   across retries of the same attempt makes Payien replay the original session
 *   instead of minting a second one; a new attempt must use a new key.
 * @returns A cancelable promise resolving to the session id, form URL, and expiry
 */
export function createPaymentMethodSession(
  agencyId: string,
  idempotencyKey?: string,
): CancelablePromise<PaymentMethodSessionResponse> {
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agencies/{agencyId}/subscription/payment-method-session',
    path: { agencyId },
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
}

/**
 * Previews the pro-rata cost of a plan change via
 * `POST /agencies/{agencyId}/subscription/change-plan/preview`, without applying it.
 *
 * @param agencyId - The agency identifier
 * @param requestBody - The candidate plan, pricing, and billing cycle
 * @returns A cancelable promise resolving to the pro-rata amount, days remaining,
 *   and projected next renewal date
 */
/**
 * Previews the effect of a plan change (proration, price) without applying it, via
 * `POST /agencies/{agencyId}/subscription/change-plan/preview`.
 *
 * @param agencyId - The agency identifier
 * @param requestBody - The prospective plan and change parameters
 * @returns A cancelable promise resolving to the preview of the plan change
 */
export function previewChangePlan(
  agencyId: string,
  requestBody: ChangePlanPreviewRequest,
): CancelablePromise<ChangePlanPreviewResponse> {
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agencies/{agencyId}/subscription/change-plan/preview',
    path: { agencyId },
    body: requestBody,
    mediaType: 'application/json',
  });
}

/**
 * Retrieves a paginated list of subscription invoices via
 * `GET /agencies/{agencyId}/subscription/invoices`.
 *
 * @param agencyId - The agency identifier
 * @param page - Zero-based page index (defaults to 0)
 * @param pageSize - Number of invoices per page (defaults to 10)
 * @returns A cancelable promise resolving to the invoices page and total count
 */
/**
 * Retrieves a paginated list of subscription invoices via
 * `GET /agencies/{agencyId}/subscription/invoices`.
 *
 * @param agencyId - The agency identifier
 * @param page = 0 - Zero-based page index
 * @param pageSize = 10 - Number of invoices per page
 * @returns A cancelable promise resolving to the paginated invoices
 */
export function getSubscriptionInvoices(
  agencyId: string,
  page = 0,
  pageSize = 10,
): CancelablePromise<InvoicesResponse> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agencies/{agencyId}/subscription/invoices',
    path: { agencyId },
    query: { page, page_size: pageSize },
  });
}

export interface AddonItem {
  addon_id: string;
  saas_addon_id: string;
  status: string;
  price_cents: number;
  currency: string;
  billing_type: string;
  billing_frequency: string | null;
  added_at: string | null;
  validity_days: number | null;
  expires_at: string | null;
}

/**
 * Lists the add-ons attached to the agency's subscription via
 * `GET /agencies/{agencyId}/subscription/addons`.
 *
 * @param agencyId - The agency identifier
 * @returns A cancelable promise resolving to the array of active add-ons
 */
/**
 * Lists the add-ons available or attached to a subscription via
 * `GET /agencies/{agencyId}/subscription/addons`.
 *
 * @param agencyId - The agency identifier
 * @returns A cancelable promise resolving to the subscription add-on items
 */
export function listSubscriptionAddons(
  agencyId: string,
): CancelablePromise<AddonItem[]> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agencies/{agencyId}/subscription/addons',
    path: { agencyId },
  });
}

/**
 * Purchases a subscription add-on by slug via
 * `POST /agencies/{agencyId}/subscription/addons`.
 *
 * @param agencyId - The agency identifier
 * @param addonSlug - The catalog slug of the add-on to purchase
 * @returns A cancelable promise resolving to the newly attached add-on
 */
/**
 * Purchases a subscription add-on by slug via
 * `POST /agencies/{agencyId}/subscription/addons`.
 *
 * @param agencyId - The agency identifier
 * @param addonSlug - The slug of the add-on to purchase
 * @returns A cancelable promise resolving to the purchased add-on item
 */
export function purchaseSubscriptionAddon(
  agencyId: string,
  addonSlug: string,
): CancelablePromise<AddonItem> {
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agencies/{agencyId}/subscription/addons',
    path: { agencyId },
    body: { addon_slug: addonSlug },
    mediaType: 'application/json',
  });
}

/** What the plan meters, and how much of it the current period has spent. */
export interface SeatUsageSummary {
  /** The billing period the counts belong to, as `YYYY-MM`. */
  period: string;
  interaction: { used: number; total: number };
  partnership_search: { used: number; total: number };
  plan_tier: string;
  /** True while trialing: the totals are the free-period ceiling (200 / 3). */
  trial_cap?: boolean;
  /** `YYYY-MM-DD` the free period ends, or null outside the trial. */
  trial_ends_at?: string | null;
}

/**
 * Fetches the agency's metered usage for the current period via
 * `GET /agencies/{agencyId}/usage`.
 *
 * Returns the counts for the quotas the catalogue actually meters —
 * interactions and partnership searches. Seats are not here: the seat limit
 * lives on the plan and the seats in use are the agency's own member list.
 *
 * @param agencyId - The agency identifier
 * @returns A cancelable promise resolving to the usage summary
 */
export function getAgencySeatUsage(
  agencyId: string,
): CancelablePromise<SeatUsageSummary> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agencies/{agencyId}/usage',
    path: { agencyId },
  });
}

/** One closed billing cycle's consumption. */
export interface QuotaPeriodUsage {
  period: string;
  used: number;
  total: number;
  /** False when no record is stored — different from a cycle that ran at zero. */
  present: boolean;
}

/** One metered quota's current cycle and recent history. */
export interface QuotaCycleBlock {
  used: number;
  total: number;
  daily_average: number | null;
  /**
   * Null when fewer than seven days of the cycle have elapsed, when nothing
   * has been consumed, or when the projection falls at or after the cycle end.
   */
  projected_exhaustion_date: string | null;
  days_before_renewal: number | null;
  exhausted: boolean;
  current_cycle_over_quota: boolean;
  consecutive_over_quota_cycles: number;
  history: QuotaPeriodUsage[];
}

/**
 * Figures are base allowance units and exclude add-on balance, so the current
 * cycle and the history share one scale.
 */
export interface SeatUsageHistory {
  period: string;
  plan_tier: string;
  cycle_start_date: string;
  /** Null when no subscription anchor exists and the calendar month stands in. */
  cycle_end_date: string | null;
  days_elapsed: number;
  cycle_interval_months: number;
  interaction: QuotaCycleBlock;
  partnership_search: QuotaCycleBlock;
}

/**
 * Fetches the seat's burn-rate projection and prior-cycle history via
 * `GET /agencies/{agencyId}/usage/history`.
 *
 * @param agencyId - The agency identifier
 * @returns A cancelable promise resolving to the usage history
 */
export function getAgencySeatUsageHistory(
  agencyId: string,
): CancelablePromise<SeatUsageHistory> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agencies/{agencyId}/usage/history',
    path: { agencyId },
  });
}

/** The codes `POST .../subscription/activate-now` answers with, one per outcome. */
export type ActivateNowResultCode =
  | 'activated'
  | 'payment_declined'
  | 'activation_in_progress'
  | 'not_in_trial'
  | 'payment_method_missing'
  | 'billing_snapshot_missing'
  | 'error';

/** The body of every answer, success or not: `result` is always there. */
export interface ActivateNowResponse {
  result: ActivateNowResultCode;
  subscription_status?: string;
  current_cycle_start_date?: string;
  current_cycle_end_date?: string;
  charged_amount_cents?: number | null;
  decline_code?: string;
  decline_message?: string;
}

/**
 * Ends the free trial and charges the plan today
 * (`POST /agencies/{agencyId}/subscription/activate-now`, no body).
 *
 * Non-200 answers (402, 409, 422, 502) reject with an `ApiError` whose `body`
 * is the same `ActivateNowResponse` shape.
 *
 * @param agencyId - The agency identifier
 * @returns A cancelable promise resolving to the activation result
 */
export function activateSubscriptionNow(
  agencyId: string,
): CancelablePromise<ActivateNowResponse> {
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agencies/{agencyId}/subscription/activate-now',
    path: { agencyId },
  });
}
